import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod/v3';
import { userApi } from '@/api/admin/users';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { FileUpload } from '@/components/ui/file-upload';
import { Form, FormControl, FormItem, FormLabel } from '@/components/ui/form';
import { FormError } from '@/components/ui/form-error';
import { Input } from '@/components/ui/input';
import { FormSkeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/context/auth-context';
import { useToast } from '@/hooks/use-toast';

// Minimal schema - only validates image since all other fields are disabled
const schema = z.object({
  image: z.any().optional(),
});

type FormData = z.infer<typeof schema>;

export function UserProfilePage() {
  const { user, refreshProfile } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: userData, isLoading } = useQuery({
    queryKey: ['user', user?.id],
    queryFn: () => userApi.findOne(user?.id!),
    enabled: !!user?.id,
  });

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      image: undefined,
    },
  });

  // Update mutation - uses presigned URL flow for avatar upload
  const updateMutation = useMutation({
    mutationFn: async (files: File[]) => {
      const file = files[0];
      // Step 1: Get presigned upload URL from backend
      const { uploadUrl, key } = await userApi.prepareAvatarUpload({
        userId: user?.id!,
        fileName: file.name,
        contentType: file.type,
      });

      // Step 2: Upload directly to S3 using presigned URL
      const s3Response = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!s3Response.ok) {
        throw new Error('Falha ao enviar imagem para S3');
      }

      // Step 3: Confirm upload with backend (saves metadata to MongoDB)
      await userApi.confirmAvatarUpload({
        userId: user?.id!,
        key,
      });

      return key;
    },
    onSuccess: async () => {
      await refreshProfile(); // Update auth context
      queryClient.invalidateQueries({ queryKey: ['user', user?.id] });
      toast({
        title: 'Sucesso',
        description: 'Avatar atualizado com sucesso.',
        variant: 'success',
      });
    },
  });

  const onSubmit = (data: FormData) => {
    if (data.image && data.image.length > 0) {
      updateMutation.mutate(data.image);
    }
  };

  // Get avatar URL asynchronously (presigned download URL)
  const [avatarUrl, setAvatarUrl] = useState<string | undefined>();

  useEffect(() => {
    if (userData?.image?.key) {
      userApi
        .getAvatarUrl(user?.id!, userData.image.key)
        .then(setAvatarUrl)
        .catch(() => setAvatarUrl(undefined));
    }
  }, [userData?.image?.key, user?.id]);

  if (isLoading) {
    return (
      <div className="w-full">
        <Card>
          <CardContent className="pt-6">
            <FormSkeleton />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Usuário não encontrado</p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Meu Perfil</h1>
        <p className="text-sm text-gray-500">Informações detalhadas sobre o seu perfil.</p>
      </div>

      {/* Form Card */}
      <Card className="w-full">
        <CardContent className="pt-6">
          <Form {...form}>
            <form noValidate onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Profiles - Display as read-only badges */}
                <div className="col-span-full">
                  <span className="block text-sm font-medium text-gray-900 mb-2">Perfis</span>
                  <div className="flex flex-wrap gap-1.5">
                    {userData.profileName && (
                      <span className="inline-flex items-center rounded text-xs font-medium bg-white text-gray-800 border border-gray-300 px-2 py-1">
                        {userData.profileName}
                      </span>
                    )}
                  </div>
                </div>

                {/* CPF - Disabled */}
                <FormItem>
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <Input value={userData.cpf} disabled />
                  </FormControl>
                </FormItem>

                {/* Name - Disabled */}
                <FormItem>
                  <FormLabel>Nome completo</FormLabel>
                  <FormControl>
                    <Input value={userData.name} disabled />
                  </FormControl>
                </FormItem>

                {/* Email - Disabled */}
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input value={userData.email} disabled />
                  </FormControl>
                </FormItem>

                {/* Username - Disabled */}
                <FormItem>
                  <FormLabel>Usuário</FormLabel>
                  <FormControl>
                    <Input value={userData.username} disabled />
                  </FormControl>
                </FormItem>
              </div>

              {/* Avatar Upload - Only editable field */}
              <Controller
                control={form.control}
                name="image"
                render={({ field }) => (
                  <FileUpload
                    label="Imagem de perfil"
                    handleFile={field.onChange}
                    multiple={false}
                    accept="jpeg, jpg, png"
                    currentImageUrl={avatarUrl}
                  />
                )}
              />

              <FormError error={updateMutation.error} onDismiss={updateMutation.reset} />

              {/* Submit Button */}
              <div className="flex gap-3 justify-end pt-4">
                <Button type="submit" disabled={updateMutation.isPending}>
                  {updateMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </motion.div>
  );
}
